// React
import * as React from "react";
import _ from 'lodash';

// Material UI components
import { validateHighlightsWithMsg } from "../Gauges/GaugesHelper";

// Internal components
import * as WidgetsThingsStructs from "../WidgetsThingsStructs";
import * as CanvasGauges from "canvas-gauges";
import { IWidgetThingsClass } from '../Utils/IWidgetThingsClass';
import { GaugeHelperHighlight, SettingsType } from '@Primitive/SettingsListType';
import { isDefined, IsNullOrEmpty, isNumeric } from '@Utils/Micro';
import { IWidgetDescription, IWidgetSettingsProps, WidgetDescriptionToPlegmaThing } from '@UI/Widgets/WidgetStructs';
import { SettingsList } from '@Primitive/SettingsList';
import { WebThing, WebThingPort } from '@API/WebThing';
import { Plegma } from '@API/Api';
import { WebThingPortType } from '@API/WebNodeTypes';
import type { LocalizationMap } from '@Primitive/Localization/Localization';
// =======================================================================================================

export interface LinearGaugeData {
    LinearGaugeSettings: LinearGaugeSettings;
}

export interface LinearGaugeSettings {
    Units: string;
    MinValue: number;
    MaxValue: number;
    MajorTicksPeriod: number;
    MajorTicks: string[];
    MinorTicks: number;
    ValueBox: boolean;
    DecimalPlaces: number;
    Highlights: GaugeHelperHighlight[];
    InnerColor: string;
    CircleColor: string;
    enableDecimal: boolean;
}

export interface LinearGaugeState { }

// ------------------------------------------------------------------------------------------------------
class LinearGauge extends React.Component<WidgetsThingsStructs.PartialIWidgetThingsProps<LinearGaugeData>, LinearGaugeState> {
    private _element: HTMLElement;
    private _container: CanvasGauges.LinearGauge;
    private _value: number;
    // ---------------------------------------------------------------------------------------------------
    constructor(props: WidgetsThingsStructs.PartialIWidgetThingsProps<LinearGaugeData>) {
        super(props);
        this._value = this.props.WidgetData.LinearGaugeSettings.MinValue;
        this.state = {};
    }
    // ---------------------------------------------------------------------------------------------------
    render() {
        return (
            <div style={{
                width: this.props.width,
                height: this.props.height,
                overflow: 'hidden',
                padding: WidgetsThingsStructs.defaultWidgetContainerPadding
            }}>
                <canvas
                    data-bar-begin-circle={"false"}
                    ref={(div) => this._element = div}
                    data-type="linear-gauge">
                </canvas>
            </div>
        );
    }
    // ---------------------------------------------------------------------------------------------------
    componentDidMount() {
        this.initLinearGauge(this.props);
    }
    // ---------------------------------------------------------------------------------------------------
    componentWillUnmount() {
        if (this._container != null)
            this._container.destroy();
    }
    // ---------------------------------------------------------------------------------------------------
    updateDataFromPort(props: WidgetsThingsStructs.PartialIWidgetThingsProps<LinearGaugeData>): boolean {
        if (_.has(props.PortsStates, PortKeys.Data)) {
            let port = props.PortsStates[PortKeys.Data];
            if (isNumeric(port.State)) {
                this._value = parseFloat(port.State);
                if (this._container != null) {
                    this._container.value = this._value;
                    if (this._container.canvas != null)
                        this._container.draw();
                    return false;
                } else
                    return true;
            }
        }
        return false;
    }
    // ---------------------------------------------------------------------------------------------------
    componentDidUpdate(prevProps: WidgetsThingsStructs.PartialIWidgetThingsProps<LinearGaugeData>) {
        let needsUpdate = false;

        if (this.props.PortVersion != prevProps.PortVersion)
            needsUpdate = this.updateDataFromPort(this.props);

        if (needsUpdate ||
            this.props.width != prevProps.width ||
            this.props.height != prevProps.height ||
            !_.isEqual(this.props.WidgetData, prevProps.WidgetData))
            needsUpdate = true;

        if (needsUpdate)
            this._container.update(this._getOptions(this.props));
    }
    // ---------------------------------------------------------------------------------------------------
    initLinearGauge(props: WidgetsThingsStructs.PartialIWidgetThingsProps<LinearGaugeData>) {
        if (this._container != null) {
            this.updateDataFromPort(props);
            this._container.update(this._getOptions(props));
        } else {
            this.updateDataFromPort(props);
            this._container = new CanvasGauges.LinearGauge(this._getOptions(props)).draw();

            // WORKAROUND: Update the value and after the first drawing
            // it seem the library have a timing issue
            setTimeout(() => {
                this.updateDataFromPort(props);
            }, 30);
        }
    }
    // ---------------------------------------------------------------------------------------------------
    private _getOptions(props: WidgetsThingsStructs.PartialIWidgetThingsProps<LinearGaugeData>): CanvasGauges.LinearGaugeOptions {
        const settings = props.WidgetData.LinearGaugeSettings;
        return {
            width: props.width,
            height: props.height,
            renderTo: this._element,
            units: settings.Units,
            minValue: settings.MinValue,
            maxValue: settings.MaxValue,
            valueBox: settings.ValueBox,
            valueInt: 1,
            valueDec: settings.DecimalPlaces,
            majorTicks: settings.MajorTicks,
            minorTicks: settings.MinorTicks,
            value: this._value,
            highlights: settings.Highlights,
            strokeTicks: true,
            colorPlate: 'transparent',
            borderShadowWidth: 0,
            borders: false,
            colorBar: settings.InnerColor,
            colorBarProgress: settings.CircleColor,
            animationDuration: 600,
            animationRule: "dequad",
            barWidth: 10
        };
    }
    // ---------------------------------------------------------------------------------------------------
}

// =======================================================================================================
interface LinearGaugeWidgetSettingsState {
    widgetData: LinearGaugeData;
}

class LinearGaugeWidgetSettings extends React.Component<IWidgetSettingsProps<LinearGaugeData>, LinearGaugeWidgetSettingsState> {
    // ---------------------------------------------------------------------------------------------------
    constructor(props: IWidgetSettingsProps<LinearGaugeData>) {
        super(props);

        this.state = { widgetData: _.cloneDeep(this.props.WidgetData) };
        this.saveData = this.saveData.bind(this);
    }
    // ---------------------------------------------------------------------------------------------------
    saveData() {
        if (isDefined(this.props.onWidgetDataUpdate)) {
            const settings = this.state.widgetData.LinearGaugeSettings;

            let majorTicks: string[] = [settings.MinValue.toString()];
            let i: number = settings.MinValue + settings.MajorTicksPeriod;

            while (i < settings.MaxValue) {
                majorTicks.push(i.toString());
                i = i + settings.MajorTicksPeriod;
            }

            majorTicks.push(settings.MaxValue.toString());

            this.setState((s: LinearGaugeWidgetSettingsState) => {
                s.widgetData.LinearGaugeSettings.MajorTicks = majorTicks;
                this.props.onWidgetDataUpdate(s.widgetData);
                return s;
            });
        }
    }
    // ---------------------------------------------------------------------------------------------------
    render() {
        return <SettingsList
            AutoSave={true}
            Data={this.state.widgetData}
            SettingsMap={[
                {
                    name: '',
                    elements: [{
                        type: SettingsType.String,
                        label: 'Units',
                        dataPath: 'LinearGaugeSettings.Units',
                        tooltip: 'Units symbol to be shown'
                    },
                    {
                        type: SettingsType.Number,
                        label: 'Min Value',
                        dataPath: 'LinearGaugeSettings.MinValue',
                        isDecimal: true,
                        oneLine: true,
                        tooltip: 'Minimum value of gauge.'
                    },
                    {
                        type: SettingsType.Number,
                        label: 'Max Value',
                        dataPath: 'LinearGaugeSettings.MaxValue',
                        isDecimal: true,
                        oneLine: true,
                        tooltip: 'Maximum value of gauge.'
                    },
                    {
                        type: SettingsType.Number,
                        label: 'Major Ticks Period',
                        dataPath: 'LinearGaugeSettings.MajorTicksPeriod',
                        oneLine: true,
                        tooltip: 'Set range of the displayed values e.g. (For values 0-100, with major ticks period set 10, values will be displayed 0,10,20,...,100).'
                    },
                    {
                        type: SettingsType.Number,
                        label: 'Minor Ticks',
                        dataPath: 'LinearGaugeSettings.MinorTicks',
                        oneLine: true,
                        tooltip: 'Set the number of ticks between 2 values of the gauge.'
                    },
                    {
                        type: SettingsType.Boolean,
                        label: 'Value Box',
                        dataPath: 'LinearGaugeSettings.ValueBox',
                        tooltip: 'Whether to show a box with the gauge value.'
                    },
                    {
                        type: SettingsType.Number,
                        label: 'Decimal Places',
                        isHidden: (data: LinearGaugeData) => !data.LinearGaugeSettings.ValueBox,
                        dataPath: 'LinearGaugeSettings.DecimalPlaces',
                        tooltip: 'The number of digits after the decimal point.',
                        minValue: 0,
                        maxValue: 20,
                        oneLine: true,
                    },
                    {
                        type: SettingsType.ColorPicker,
                        label: 'Inner Color',
                        dataPath: 'LinearGaugeSettings.InnerColor',
                        tooltip: "Color used above the data's number."
                    },
                    {
                        type: SettingsType.ColorPicker,
                        label: 'Circle Color',
                        dataPath: 'LinearGaugeSettings.CircleColor',
                        tooltip: "Color used below the data's number."
                    },
                    {
                        type: SettingsType.Boolean,
                        label: 'Decimal Highlights',
                        dataPath: 'LinearGaugeSettings.enableDecimal',
                        tooltip: 'Whether to enable decimal values in from and to values of highlights.'
                    },
                    {
                        type: SettingsType.Highlights,
                        label: 'Highlights',
                        dataPath: 'LinearGaugeSettings.Highlights',
                        tooltip: "Color to use based on gauge's value range.",
                        icon: <i className="fas fa-pen" style={{ fontSize: 16 }} />,
                        enableDecimal: 'LinearGaugeSettings.enableDecimal',
                        minValue: 'LinearGaugeSettings.MinValue',
                        maxValue: 'LinearGaugeSettings.MaxValue'
                    }]
                }]}
            onCheckData={(data) => {
                const settings = data.LinearGaugeSettings;
                // todo: use Micro.ts function (if we don't have this kind of helper function, export to Micro.ts)
                const isNumber = (v) => {
                    return !isNaN(parseFloat(v)) && isFinite(v);
                };
                const periodsNum = (settings.MaxValue - settings.MinValue)
                    / settings.MajorTicksPeriod;

                let isValid: boolean = true;
                let errorMsg: string = '';

                if (!isNumber(settings.MinValue)) {
                    errorMsg = 'Min Value should be numeric';
                    isValid = false;
                }
                else if (!isNumber(settings.MaxValue)) {
                    errorMsg = 'Max Value should be numeric';
                    isValid = false;
                }
                else if (!isNumber(settings.MajorTicksPeriod)) {
                    errorMsg = 'Major Ticks Period should be numeric';
                    isValid = false;
                }
                else if (!isNumber(settings.MinorTicks)) {
                    errorMsg = 'Minor Ticks should be numeric';
                    isValid = false;
                }
                else if (settings.MinValue > settings.MaxValue) {
                    errorMsg = 'Max Value should be greater than Min Value';
                    isValid = false;
                }
                else if (periodsNum !== parseInt(periodsNum.toString())) {
                    errorMsg = 'Major Ticks Period must be the integer divider \
                        of the difference between Max and Min values';
                    isValid = false;
                }
                else {
                    const highlightsRes = validateHighlightsWithMsg(settings.MinValue, settings.MaxValue, settings.Highlights);
                    if (!highlightsRes.valid)
                        return highlightsRes;
                }

                return {
                    valid: isValid,
                    errorMsg: errorMsg
                };
            }}
            onSaveData={(data) => {
                this.setState(
                    { widgetData: data },
                    () => this.saveData());
            }}
            Toolbar={this.props.SettingListToolbar}
        />;
    }
    // ---------------------------------------------------------------------------------------------------
}
// =======================================================================================================
// Gauge Description interface
const enum PortKeys {
    Data = "Data"
}
export class LinearGaugeWidget extends IWidgetThingsClass<LinearGaugeData> {
    // ------------------------------------------------------------------------------------------------
    public Render(key: string, width: number, height: number): React.ReactNode {
        return <LinearGauge />;
    }
    // ------------------------------------------------------------------------------------------------
    public RenderWidgetSettings(key: string, width: number, height: number): React.ReactElement<any> {
        const parent = this.Parent;
        return <LinearGaugeWidgetSettings
            key={"Settings-" + key}
            width={width}
            height={height}
            WidgetData={_.cloneDeep(this.WidgetData)} // copy of the data and is return the copy in the update cb
            WidgetDefaultValues={_.cloneDeep(this.Description.DefaultValues)}
            onWidgetDataUpdate={(d) => this.WidgetDataUpdate(d)}
            ThingKey={this.ThingKey}
            NodeKey={this.NodeKey}
            SettingListToolbar={isDefined(parent) ? parent.SettingsListToolbar : null}
        />;
    }
    // ------------------------------------------------------------------------------------------------
    initThing() {
        // Thing description
        let plegmaThing = WidgetDescriptionToPlegmaThing(this.Description);

        // Ports
        let ports: WebThingPort[] = [];
        ports.push(new WebThingPort(
            {
                Name: "Data",
                PortKey: PortKeys.Data,
                Type: Plegma.ePortType.Decimal,
                ioDirection: Plegma.ioPortDirection.Input,
                ConfFlags: Plegma.ePortConf.None,
                Description: "",
                State: ""
            },
            WebThingPortType.Data));
        // Create the thing
        let thing = new WebThing(this.WebModule, plegmaThing, ports);
        this.ThingKey = thing.ThingKey;
    }
    // ------------------------------------------------------------------------------------------------
    public static GetDefaultWidgetDataStatic(): LinearGaugeData {
        return _.cloneDeep({
            LinearGaugeSettings: {
                Units: "%",
                MinValue: 0,
                MaxValue: 100,
                ValueBox: true,
                DecimalPlaces: 2,
                MajorTicksPeriod: 10,
                MajorTicks: [],
                MinorTicks: 2,
                Highlights: [],
                InnerColor: "rgba(204,204,204,1)",
                CircleColor: "rgba(136,136,136,1)",
                enableDecimal: false
            }
        });
    }
    // ------------------------------------------------------------------------------------------------
    public GetDefaultWidgetData() { return LinearGaugeWidget.GetDefaultWidgetDataStatic(); }
    // ------------------------------------------------------------------------------------------------
    public static GetWidgetDesc(): IWidgetDescription {
        let data: LinearGaugeData = LinearGaugeWidget.GetDefaultWidgetDataStatic();

        // fill sample ticks
        data.LinearGaugeSettings.MajorTicks = ["0", "10", "20", "30", "40", "50", "60", "70", "80", "90", "100"];

        // fill sample highlights
        data.LinearGaugeSettings.Highlights = [{
            "from": 80,
            "to": 100,
            "color": "rgba(200,50,50,.75)"
        }];

        return {
            Title: "Linear Gauge",
            WidgetTitle: "",
            Key: "LinearGaugeWidget",
            Type: "LinearGaugeWidget",
            Data: data,
            Description: "Linear Gauge widget",
            MinH: 1,
            MinW: 1,
            ShowExtraMenu: false,
            IconURI: WidgetsThingsStructs.IconURIPath + "/png/linear_gauge.png"
        };
    }
    // ------------------------------------------------------------------------------------------------
    public GetLocalizationDictionary(): LocalizationMap {
        if (!IsNullOrEmpty(this.Description.WidgetTitle))
            return {
                [WidgetsThingsStructs.WidgetLocalizationKeys.Title]: this.Description.WidgetTitle
            };
        else
            // nothing to expose
            return {};
    };
    // ------------------------------------------------------------------------------------------------
    GetDataPortKeyStatic(): string { return PortKeys.Data; };
    // ------------------------------------------------------------------------------------------------
}
// =======================================================================================================